/**
 * NOS-124: Media videos CRUD + related taxonomies for the dashboard editor.
 * RLS enforces can_manage_media.
 */

import { useCallback, useEffect, useState } from "react"
import { supabase } from "@/lib/supabase"
import type { Database } from "@/types/database"
import { generateSlug } from "@/lib/slugUtils"
import { queuePublicCachePurge } from "@/lib/publicCache"

type MediaVideo = Database["public"]["Tables"]["media_videos"]["Row"]
type MediaVideoInsert = Database["public"]["Tables"]["media_videos"]["Insert"]
type MediaVideoUpdate = Database["public"]["Tables"]["media_videos"]["Update"]
type MediaSeries = Database["public"]["Tables"]["media_series"]["Row"]

export type MediaTab = "published" | "drafts"

type SpeakerJoin = {
  speaker_id: string
  media_speakers: { id: string; name: string } | null
}
type TopicJoin = {
  topic_id: string
  media_topics: { id: string; name: string; slug: string } | null
}

export interface MediaVideoWithRelations extends MediaVideo {
  media_series: Pick<MediaSeries, "id" | "title" | "slug"> | null
  media_video_speakers: SpeakerJoin[] | null
  media_video_topics: TopicJoin[] | null
}

const LIST_SELECT = `
  *,
  media_series ( id, title, slug ),
  media_video_speakers (
    speaker_id,
    media_speakers ( id, name )
  ),
  media_video_topics (
    topic_id,
    media_topics ( id, name, slug )
  )
`

function isPublishedRow(v: MediaVideo): boolean {
  return v.is_published && Boolean(v.published_at) && new Date(v.published_at!) <= new Date()
}

export function speakerNames(v: MediaVideoWithRelations): string {
  const rows = v.media_video_speakers ?? []
  return rows
    .map((r) => r.media_speakers?.name)
    .filter(Boolean)
    .join(", ")
}

export function useMediaVideos(orgId: string | null, tab: MediaTab) {
  const [videos, setVideos] = useState<MediaVideoWithRelations[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    if (!orgId) {
      setVideos([])
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)
    try {
      const { data, error: fetchError } = await supabase
        .from("media_videos")
        .select(LIST_SELECT)
        .eq("org_id", orgId)
        .order("updated_at", { ascending: false })

      if (fetchError) {
        setError(fetchError.message)
        setVideos([])
        return
      }
      const rows = (data ?? []) as MediaVideoWithRelations[]
      const filtered =
        tab === "published" ? rows.filter(isPublishedRow) : rows.filter((r) => !isPublishedRow(r))
      setVideos(filtered)
    } finally {
      setLoading(false)
    }
  }, [orgId, tab])

  useEffect(() => {
    refresh()
  }, [refresh])

  return { videos, loading, error, refresh }
}

export function useMediaVideo(orgId: string | null, videoId: string | null) {
  const [video, setVideo] = useState<MediaVideoWithRelations | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    if (!orgId || !videoId) {
      setVideo(null)
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)
    const { data, error: fetchError } = await supabase
      .from("media_videos")
      .select(LIST_SELECT)
      .eq("id", videoId)
      .eq("org_id", orgId)
      .maybeSingle()

    if (fetchError) {
      setError(fetchError.message)
      setVideo(null)
    } else {
      setVideo((data as MediaVideoWithRelations) ?? null)
    }
    setLoading(false)
  }, [orgId, videoId])

  useEffect(() => {
    refresh()
  }, [refresh])

  return { video, loading, error, refresh }
}

export interface SaveMediaVideoInput {
  title: string
  slug: string
  description?: string | null
  youtube_url?: string | null
  youtube_video_id?: string | null
  thumbnail_url?: string | null
  audio_url?: string | null
  series_id?: string | null
  speaker_ids: string[]
  topic_ids: string[]
  scripture_passage?: string | null
  is_published: boolean
  published_at?: string | null
  duration_seconds?: number | null
}

async function replaceVideoSpeakers(videoId: string, speakerIds: string[]) {
  await supabase.from("media_video_speakers").delete().eq("video_id", videoId)
  if (speakerIds.length > 0) {
    await supabase.from("media_video_speakers").insert(
      speakerIds.map((speaker_id) => ({ video_id: videoId, speaker_id }))
    )
  }
}

async function replaceVideoTopics(videoId: string, topicIds: string[]) {
  await supabase.from("media_video_topics").delete().eq("video_id", videoId)
  if (topicIds.length > 0) {
    await supabase.from("media_video_topics").insert(
      topicIds.map((topic_id) => ({ video_id: videoId, topic_id }))
    )
  }
}

export function useCreateMediaVideo(orgId: string | null) {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const createMediaVideo = useCallback(
    async (input: SaveMediaVideoInput): Promise<MediaVideo | null> => {
      if (!orgId) return null
      setLoading(true)
      setError(null)
      const slug = input.slug.trim() || generateSlug(input.title) || "video"
      let publishedAt = input.published_at ?? null
      if (input.is_published && !publishedAt) {
        publishedAt = new Date().toISOString()
      }
      if (!input.is_published) {
        publishedAt = null
      }

      const insert: MediaVideoInsert = {
        org_id: orgId,
        title: input.title.trim(),
        slug,
        description: input.description?.trim() || null,
        youtube_url: input.youtube_url?.trim() || null,
        youtube_video_id: input.youtube_video_id?.trim() || null,
        thumbnail_url: input.thumbnail_url?.trim() || null,
        audio_url: input.audio_url?.trim() || null,
        series_id: input.series_id || null,
        scripture_passage: input.scripture_passage?.trim() || null,
        is_published: input.is_published,
        published_at: publishedAt,
        duration_seconds: input.duration_seconds ?? null,
      }

      const { data, error: insertError } = await supabase
        .from("media_videos")
        .insert(insert)
        .select()
        .single()

      if (insertError || !data) {
        setLoading(false)
        setError(
          insertError?.code === "23505"
            ? "A video with this URL slug already exists."
            : insertError?.message ?? "Failed to create video"
        )
        return null
      }

      const row = data as MediaVideo
      await replaceVideoSpeakers(row.id, input.speaker_ids)
      await replaceVideoTopics(row.id, input.topic_ids)

      setLoading(false)
      if (row.is_published) {
        queuePublicCachePurge(orgId, { type: "media-save", videoSlug: row.slug })
      }
      return row
    },
    [orgId]
  )

  return { createMediaVideo, loading, error }
}

export function useUpdateMediaVideo(orgId: string | null) {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const updateMediaVideo = useCallback(
    async (id: string, input: SaveMediaVideoInput): Promise<boolean> => {
      if (!orgId) return false
      setLoading(true)
      setError(null)
      let publishedAt = input.published_at ?? null
      if (input.is_published && !publishedAt) {
        publishedAt = new Date().toISOString()
      }
      if (!input.is_published) {
        publishedAt = null
      }

      const update: MediaVideoUpdate = {
        title: input.title.trim(),
        slug: input.slug.trim() || generateSlug(input.title),
        description: input.description?.trim() || null,
        youtube_url: input.youtube_url?.trim() || null,
        youtube_video_id: input.youtube_video_id?.trim() || null,
        thumbnail_url: input.thumbnail_url?.trim() || null,
        audio_url: input.audio_url?.trim() || null,
        series_id: input.series_id || null,
        scripture_passage: input.scripture_passage?.trim() || null,
        is_published: input.is_published,
        published_at: publishedAt,
        duration_seconds: input.duration_seconds ?? null,
        updated_at: new Date().toISOString(),
      }

      const { error: updateError } = await supabase
        .from("media_videos")
        .update(update)
        .eq("id", id)
        .eq("org_id", orgId)

      if (updateError) {
        setError(
          updateError.code === "23505"
            ? "A video with this URL slug already exists."
            : updateError.message
        )
        setLoading(false)
        return false
      }

      await replaceVideoSpeakers(id, input.speaker_ids)
      await replaceVideoTopics(id, input.topic_ids)

      setLoading(false)
      queuePublicCachePurge(orgId, {
        type: "media-save",
        videoSlug: update.slug ?? generateSlug(input.title),
      })
      return true
    },
    [orgId]
  )

  return { updateMediaVideo, loading, error }
}

export function useDeleteMediaVideo(orgId: string | null) {
  const [loading, setLoading] = useState(false)

  const deleteMediaVideo = useCallback(
    async (id: string): Promise<boolean> => {
      if (!orgId) return false
      setLoading(true)
      const { data, error: delError } = await supabase
        .from("media_videos")
        .delete()
        .eq("id", id)
        .eq("org_id", orgId)
        .select("slug")
        .maybeSingle()
      setLoading(false)
      const slug = (data as { slug: string } | null)?.slug
      if (!delError && slug) {
        queuePublicCachePurge(orgId, { type: "media-delete", videoSlug: slug })
      }
      return !delError
    },
    [orgId]
  )

  return { deleteMediaVideo, loading }
}

/** Fire-and-forget transcription + AI content pipeline (NOS-126). */
export function invokeTranscribeMedia(videoId: string, orgId: string): void {
  void supabase.functions
    .invoke("transcribe-media", {
      body: { video_id: videoId, org_id: orgId },
    })
    .then(({ error }) => {
      if (error) console.warn("transcribe-media invoke:", error.message)
    })
}

export function maybeInvokeTranscribeAfterSave(
  videoId: string,
  orgId: string,
  youtubeVideoId: string | null | undefined,
  transcriptStatus: string | null | undefined
): void {
  if (youtubeVideoId && transcriptStatus === "none") {
    invokeTranscribeMedia(videoId, orgId)
  }
}
